import { CACHE_KEY_COMPANY } from "../../../../utils/constants";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CompanyInterface } from "../../../../models/GeneralModule/Company";
import ToastMessage from "../../../../common/ToastMessage";
import companyServices from "../../../../services/general/companyServices";

interface DeleteCompanyContext {
  previousCompanies: CompanyInterface[];
  deletedCompany: CompanyInterface | undefined;
}

const useDeleteCompany = (page: number, pageSize: number) => {
  const queryClient = useQueryClient();

  return useMutation<
    void, // No return data after deletion
    Error,
    string, // ID of the department to delete
    DeleteCompanyContext
  >({
    mutationFn: companyServices.Remove, // Remove should take a string ID

    onMutate: (companyId: string) => {
      const previousCompanies =
        queryClient.getQueryData<CompanyInterface[]>([
          CACHE_KEY_COMPANY,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ]) || [];

      // Optimistically update the cache
      queryClient.setQueryData<CompanyInterface[]>(
        [
          CACHE_KEY_COMPANY,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        previousCompanies.filter((company) => company.id !== companyId) // Use companyId to filter
      );

      ToastMessage.Success();
      return {
        previousCompanies,
        deletedCompany: previousCompanies.find(
          (company) => company.id === companyId
        ),
      };
    },

    onError: (error, companyId, context) => {
      ToastMessage.Failed(error.message);
      if (!context) return;

      // Rollback to the previous state in case of error
      queryClient.setQueryData<CompanyInterface[]>(
        [
          CACHE_KEY_COMPANY,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        context.previousCompanies
      );
    },

    onSuccess: () => {
      // Optionally, you could perform additional actions on success
      // For example, invalidating queries or updating other parts of the state
    },
  });
};

export default useDeleteCompany;
